/**
 * Модалка группового выполнения событий
 * Позволяет заполнить атрибуты для нескольких событий одновременно
 */
/** @jsxImportSource @emotion/react */
import React, { useState, useMemo } from 'react';
import { Modal, Form, Input, DatePicker, Button, Space, Alert, Typography, Spin } from 'antd';
import dayjs from 'dayjs';
import { skipToken } from '@reduxjs/toolkit/query';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import { useCompleteEventMutation } from '../../api/plantingEventsApi';
import { useGetEventTemplateByIdQuery } from '../../api/eventTemplatesApi';
import { toastError, toastSuccess } from '../../utils/toast';
import type { BatchChildEvent } from '../../types/plantingEvents';
import { useSave } from '../../contexts/WorldContext';
import { AttributeInput } from './AttributeInput';

const { TextArea } = Input;
const { Text } = Typography;

const styles = {
  loadingContainer: css({ marginTop: 16 }),
};

interface BulkCompleteEventsModalProps {
  open: boolean;
  events: BatchChildEvent[];
  templateId: number;
  templateName: string;
  farmId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const BulkCompleteEventsModal: React.FC<BulkCompleteEventsModalProps> = ({
  open,
  events,
  templateId,
  templateName,
  farmId,
  onClose,
  onSuccess,
}) => {
  const save = useSave();
  const [form] = Form.useForm();
  const [completeEvent] = useCompleteEventMutation();
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  // Загружаем шаблон для получения атрибутов
  const { data: templateDetail, isLoading: loadingTemplate } = useGetEventTemplateByIdQuery(
    templateId && open && save?.id ? { id: templateId, saveId: save.id } : skipToken
  );

  // Получаем атрибуты из шаблона
  const attributes = useMemo(() => {
    if (loadingTemplate) return [];
    if (templateDetail?.properties && templateDetail.properties.length > 0) {
      return templateDetail.properties.map((attr) => ({
        propertyId: attr.propertyId,
        name: attr.name,
        dataType: attr.dataType,
        unit: attr.unit,
        isRequired: attr.isRequired,
      }));
    }
    return [];
  }, [templateDetail, loadingTemplate]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setProcessing(true);

      let successCount = 0;
      let errorCount = 0;

      // Последовательно выполняем события
      for (let i = 0; i < events.length; i++) {
        const event = events[i];
        setProgress({ current: i + 1, total: events.length });

        try {
          // Формируем payload для каждого события
          const attributesPayload: { [key: string]: number | string | boolean } = {};
          
          attributes.forEach((attr) => {
            const fieldValue = values[`attr_${attr.propertyId}`];
            if (fieldValue !== undefined && fieldValue !== null) {
              attributesPayload[String(attr.propertyId)] = fieldValue;
            }
          });

          const payload = {
            actualDate: values.actualDate
              ? dayjs(values.actualDate).format('YYYY-MM-DD')
              : dayjs().format('YYYY-MM-DD'),
            notes: values.notes || undefined,
            attributes: Object.keys(attributesPayload).length > 0 ? attributesPayload : undefined,
          };

          await completeEvent({
            saveId: save.id,
            eventId: event.id,
            payload,
          }).unwrap();

          successCount++;
        } catch (error) {
          console.error(`Error completing event ${event.id}:`, error);
          errorCount++;
        }
      }

      setProcessing(false);

      if (errorCount === 0) {
        toastSuccess(`Успешно выполнено ${successCount} событий`);
      } else {
        toastError(
          `Выполнено ${successCount} событий, ошибок: ${errorCount}`
        );
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error in bulk complete:', error);
      setProcessing(false);
      toastError('Ошибка при групповом выполнении');
    }
  };

  return (
    <Modal
      open={open}
      title={`Групповое выполнение: ${templateName}`}
      onCancel={onClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={processing}>
          Отмена
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={processing}
          disabled={loadingTemplate}
        >
          {processing
            ? `Выполнение ${progress.current}/${progress.total}...`
            : `Выполнить (${events.length} шт.)`}
        </Button>,
      ]}
    >
      {loadingTemplate ? (
        <div css={css([commonStyles.textCenter, { padding: '40px' }])}>
          <Spin size="large" />
          <div css={styles.loadingContainer}>Загрузка шаблона...</div>
        </div>
      ) : (
        <Space direction="vertical" size="middle" css={commonStyles.fullWidth}>
          <Alert
            type="info"
            message={`Выбрано событий: ${events.length}`}
            description="Все выбранные события будут выполнены с одинаковыми значениями атрибутов."
            showIcon
          />

          <Form form={form} layout="vertical">
            {/* Фактическая дата */}
            <Form.Item
              label="Фактическая дата выполнения"
              name="actualDate"
              initialValue={dayjs()}
              rules={[{ required: true, message: 'Укажите дату' }]}
            >
              <DatePicker css={commonStyles.fullWidth} format="DD.MM.YYYY" />
            </Form.Item>

            {/* Атрибуты шаблона */}
            {attributes.map((attr) => (
              <Form.Item
                key={attr.propertyId}
                label={
                  <Space>
                    <Text>{attr.name}</Text>
                    {attr.unit && <Text type="secondary">({attr.unit})</Text>}
                  </Space>
                }
                name={`attr_${attr.propertyId}`}
                rules={[
                  {
                    required: attr.isRequired,
                    message: `Заполните ${attr.name}`,
                  },
                ]}
              >
                <AttributeInput dataType={attr.dataType} />
              </Form.Item>
            ))}

            {/* Примечания */}
            <Form.Item label="Примечания" name="notes">
              <TextArea rows={3} placeholder="Общие заметки для всех событий" />
            </Form.Item>
          </Form>
        </Space>
      )}
    </Modal>
  );
};
